import { useEffect, useRef } from 'react';
import { useInViewport, useSafeState } from 'ahooks';
import { Options } from 'ahooks/lib/useInViewport';

const defaultIntersectionInit = { root: null, rootMargin: '0px', threshold: 1 };
const useInfiniteLoad = <E extends HTMLElement>(
  fetch: (() => Promise<unknown>) | null,
  key: false | number | string | undefined,
  options: Options = defaultIntersectionInit,
) => {
  const ref = useRef<E>(null);
  const lastKeyRef = useRef<false | number | string | undefined>(key);
  const [inViewport] = useInViewport(ref, options);

  const fetchRef = useRef(fetch);
  const [loading, setLoading] = useSafeState(false);

  const isUndefined = key === undefined;
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);
  useEffect(
    () => {
      const skip = isUndefined || key === false || !ref.current || !inViewport || loading;
      if (skip || !fetchRef.current) {
        return;
      }
      lastKeyRef.current = key;
      const { current: fetch } = fetchRef;
      setLoading(true);
      fetch().finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 800);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, key, inViewport],
  );
  const hasMore = key !== false;
  let status: 'loading' | 'no_more' | 'idle' = 'idle';
  if (loading) {
    status = 'loading';
  } else if (hasMore) {
    status = 'idle';
  } else {
    status = 'no_more';
  }
  return {
    status,
    loading,
    ref,
  };
};

export default useInfiniteLoad;
